import React from 'react';
import Avatar from '../../../../components/shared/Avatar/Avatar';
import { FeedPost } from '../../../types/feed';
import { formatRelativeTime } from '../../../utils/formatRelativeTime';
import './FeedPostCard.css';

interface FeedPostCardProps {
  post: FeedPost;
  onLike?: (postId: string) => void;
  onComment?: (postId: string) => void;
  onShare?: (postId: string) => void;
}

const FeedPostCard: React.FC<FeedPostCardProps> = ({
  post,
  onLike,
  onComment,
  onShare,
}) => {
  const [replyText, setReplyText] = React.useState('');
  const avatarSrc = post.author.avatarUrl || post.author.avatarEmoji;
  const galleryImages = post.imageUrls || (post.imageUrl ? [post.imageUrl] : []);
  const canReply = replyText.trim().length > 0;

  const handleReplySubmit = () => {
    if (!canReply) return;
    onComment?.(post.id);
    setReplyText('');
  };

  return (
    <article className="v2-feed-post-card">
      <header className="v2-feed-post-card__header">
        <div className="v2-feed-post-card__author">
          <Avatar
            src={avatarSrc}
            alt={post.author.name}
            size="medium"
            className="v2-feed-post-card__avatar"
          />
          <div className="v2-feed-post-card__author-info">
            <span className="v2-feed-post-card__author-name">{post.author.name}</span>
            <span className="v2-feed-post-card__location">{post.location}</span>
          </div>
        </div>
        <time className="v2-feed-post-card__time" dateTime={post.createdAt}>
          {formatRelativeTime(post.createdAt)}
        </time>
        {post.badge && (
          <button type="button" className="v2-feed-post-card__badge">
            {post.badge}
          </button>
        )}
      </header>

      {(galleryImages.length > 0 || post.imageEmoji) && (
        <div className={`v2-feed-post-card__media ${galleryImages.length > 1 ? 'v2-feed-post-card__media--gallery' : ''}`}>
          {galleryImages.length > 0 ? (
            galleryImages.slice(0, 3).map((imageUrl, index) => (
              <div key={imageUrl} className={`v2-feed-post-card__image-wrap v2-feed-post-card__image-wrap--${index + 1}`}>
                <img src={imageUrl} alt="" className="v2-feed-post-card__image" />
                {index === 2 && post.savedCount && (
                  <span className="v2-feed-post-card__more">+{post.savedCount}</span>
                )}
              </div>
            ))
          ) : (
            <span className="v2-feed-post-card__image-placeholder">{post.imageEmoji}</span>
          )}
        </div>
      )}

      <p className="v2-feed-post-card__caption">{post.caption}</p>

      <footer className="v2-feed-post-card__actions">
        <button
          type="button"
          className={`v2-feed-post-card__action ${post.isLiked ? 'v2-feed-post-card__action--active' : ''}`}
          onClick={() => onLike?.(post.id)}
          aria-label={`Like post, ${post.likes} likes`}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
            <path
              d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              fill={post.isLiked ? 'currentColor' : 'none'}
            />
          </svg>
          <span>{post.likes}</span>
        </button>
        <button
          type="button"
          className="v2-feed-post-card__action"
          onClick={() => onComment?.(post.id)}
          aria-label={`Comment, ${post.comments} comments`}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
            <path
              d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <span>{post.comments} replies</span>
        </button>
        <button
          type="button"
          className="v2-feed-post-card__action"
          onClick={() => onShare?.(post.id)}
          aria-label="Share post"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
            <path
              d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <polyline
              points="16 6 12 2 8 6"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <line x1="12" y1="2" x2="12" y2="15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
          <span>Share</span>
        </button>
      </footer>

      <div className="v2-feed-post-card__reply">
        <input
          type="text"
          className="v2-feed-post-card__reply-input"
          placeholder="Write a reply..."
          value={replyText}
          onChange={(event) => setReplyText(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              handleReplySubmit();
            }
          }}
          aria-label={`Reply to ${post.author.name}`}
        />
        <button
          type="button"
          className="v2-feed-post-card__reply-submit"
          onClick={handleReplySubmit}
          disabled={!canReply}
        >
          Reply
        </button>
      </div>
    </article>
  );
};

export default FeedPostCard;
